import { z } from 'zod';
import { AddressValueSchema } from './Inputs';
import { GoalSchema } from './QrCodes';
import {
  priceLookupKeySchema,
  promoCodeTypesScema,
  subscriptionPlanSchema,
  subscriptionStatuses
} from './Subscriptions';
import { salesPointBaseSchema } from './SalesPoints';


export const SubscriptionsEnum = z.enum(['yearly', 'monthly']);

export type SubscriptionsType = z.infer<typeof SubscriptionsEnum>;

export const CreateShopSchema = z.object({
  id: z.string().uuid(),
  business_name: z.string().min(3),
  legal_name: z.string(),
  business_category: z.string().uuid(),
  vat: z.string().length(11),
  extraSalesPoints: z.array(salesPointBaseSchema),
  mainSalesPointAddress: AddressValueSchema,
  mainSalesPointName: z.string().min(2),
  subscription: z.object({ subscriptionType: SubscriptionsEnum, promoCode: z.string().optional() }),
  billing_address: AddressValueSchema,
  sdi_code: z.string().min(1)
});

export const createShopApiResultSchema = z.object({
  next: z.literal('toPayment'),
  subscriptionSummary: z.object({
    invoice: z.object({
      total_before_discount: z.number(),
      total_before_discount_excluding_tax: z.number().nullish(),
      tax_amount: z.number().nullish(),
      total: z.number(),
      total_excluding_tax: z.number().nullish(),
      discount_amount: z.number().nullish()
    }),
    client_secret: z.string(),
    status: z.enum(subscriptionStatuses),
    extra_sales_points_number: z.number(),
    subscription_amount: z.number(),
    extra_sales_points_subscriptions_amount: z.number(),
    interval: z.enum(['year', 'month', 'day', 'week'])
  })
}).or(z.object({ next: z.literal('toDashboard') }));

export type CreateShopApiResult = z.infer<typeof createShopApiResultSchema>;

export type CreateShopType = z.infer<typeof CreateShopSchema>;

export const ShopPreviewSchema = z.object({
  id: z.string().uuid(),
  business_name: z.string(),
  latest_update_at: z.string().date(),
  brand_color: z.string(),
  followers_count: z.coerce.number()
});
export type ShopPreview = z.infer<typeof ShopPreviewSchema>;

export const UserShopPreviewSchema = ShopPreviewSchema.extend({
  notifications_allowed: z.boolean(),
  points: z.coerce.number().optional(),
  goals: z.array(GoalSchema).optional(),
  created_at: z.string().nullish(),
  latest_transaction_at: z.string().nullish(),
});
export type UserShopPreview = z.infer<typeof UserShopPreviewSchema>;

export const shopQuickLinkSchema = z.object({ label: z.string(), value: z.string() });
export type ShopQuickLink = z.infer<typeof shopQuickLinkSchema>;

export const salesPointsPosition = z.object({ full_address: z.string(), google_place_id: z.string() });
export type SalesPointsPosition = z.infer<typeof salesPointsPosition>;

export const UserShopSchema = z.object({
  user_id: z.string().uuid().nullable(),
  shop_id: z.string().uuid(),
  created_at: z.string().date().nullable(),
  points: z.number(),
  latest_notification_open: z.string().date().nullable(),
  notifications_allowed: z.boolean(),
  goals: z.array(GoalSchema),
  category_it: z.string(),
  just_added: z.boolean().optional(),
  business_name: z.string(),
  latest_update_at: z.string().date(),
  brand_color: z.string(),
  bio: z.string().optional(),
  sales_points_positions: z.array(salesPointsPosition),
  phone_numbers: z.array(shopQuickLinkSchema).optional(),
  links: z.array(shopQuickLinkSchema).optional(),
  points_expiration_days: z.coerce.number().nullable(),
  followers_count: z.number(),
  isUserFollowing: z.boolean(),
});

export type UserShop = z.infer<typeof UserShopSchema>;

export const ShopAttrsSchema = z.object({
  id: z.string().uuid(),
  business_name: z.string(),
  brand_color: z.string(),
  latest_update_at: z.string().nullish(),
  onboarded: z.literal(true),
  completed: z.boolean(),
  public: z.boolean(),
  isVisible: z.boolean(),
  business_category: z.string().uuid(),
  vat: z.string().length(11),
  subscription_status: z.enum(['active', 'incomplete', 'incomplete_expired', 'trialing', 'unpaid', 'canceled', 'past_due', 'paused']).optional(),
  isSubscriptionValid: z.boolean(),
  subscription_plan: subscriptionPlanSchema,
  subscription_price_lookup_key: priceLookupKeySchema,
});

export type ShopAttrs = z.infer<typeof ShopAttrsSchema>


export const nearbyShopsSchema = z.object({
  shop_id: z.string().uuid(),
  business_name: z.string(),
  full_address: z.string(),
  google_position_id: z.string(),
  distance_meters: z.number(),
  is_following: z.boolean(),
  latest_update_at: z.string().nullable()
});

export type NearbyShops = z.infer<typeof nearbyShopsSchema>

export const ShopProfileSchema = z.object({
  brand_color: z.string(),
  points_ratio: z.coerce.number().nullish(),
  goals: z.array(GoalSchema),
  initial_points: z.coerce.number(),
  bio: z.string().nullish(),
  logo_preview_url: z.string().nullish(),
  points_expiration_days: z.coerce.number().nullable()
});

export type ShopProfile = z.infer<typeof ShopProfileSchema>


export const ShopStripeCustomerMetadata = z.object({
  business_name: z.string().nullish(),
  shop_id: z.string().uuid(),
  promo_code: z.string().nullish(),
  promo_type: promoCodeTypesScema.nullish(),
  salesman_id: z.string().nullish(),
  user_id: z.string().nullish()
});

export type ShopStripeCustomerMetadata = z.infer<typeof ShopStripeCustomerMetadata>
